import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowSquareOut,
  CaretDown,
  Gavel,
  MagnifyingGlass,
  SealCheck,
} from "@phosphor-icons/react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DataRow,
  DataTable,
  IconButton,
  LoadingOverlay,
  PaginationFooter,
  ProfileTabs,
  SearchField,
  buildPageList,
  initialsForMember,
} from "@breakingthelines/design-system";

import { handleTabArrowKeyDown } from "@/components/common/tab-keyboard-nav";
import { listUsers } from "@/lib/admin-api";

import { ScholarshipsManageView } from "./scholarships";
import styles from "./users-list.module.css";

const PER_PAGE_OPTIONS = [10, 20, 50];

const TABS = ["All", "Scholarships"] as const;
type Tab = (typeof TABS)[number];

// The ?tab= search param is what makes this page deep-linkable: the
// redirected /users/scholarships route (see router.tsx) lands here as
// /users?tab=scholarships, and this is also what a user gets if they share
// or reload the URL after switching tabs themselves.
function tabFromSearchParams(params: URLSearchParams): Tab {
  return params.get("tab")?.toLowerCase() === "scholarships" ? "Scholarships" : "All";
}

type VerifiedFilter = "all" | "verified" | "not_verified";
type BannedFilter = "all" | "banned" | "active";

const VERIFIED_OPTIONS: { value: VerifiedFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "verified", label: "Verified" },
  { value: "not_verified", label: "Not Verified" },
];

const BANNED_OPTIONS: { value: BannedFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "banned", label: "Banned" },
  { value: "active", label: "Active" },
];

function useDebouncedValue<T>(value: T, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(handle);
  }, [value, delay]);

  return debounced;
}

function formatDate(value?: string) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(date);
}

function formatTier(tier?: string) {
  switch (tier) {
    case "USER_TIER_FREE":
      return "Free";
    case "USER_TIER_PRO":
      return "Pro";
    case "USER_TIER_LINE_BREAKERS":
      return "Line Breakers";
    default:
      return "Free";
  }
}

export function UsersListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = tabFromSearchParams(searchParams);

  function handleTabChange(tab: Tab) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === "Scholarships") {
          next.set("tab", "scholarships");
        } else {
          next.delete("tab");
        }
        return next;
      },
      { replace: false },
    );
  }

  const [searchInput, setSearchInput] = useState("");
  const debouncedQuery = useDebouncedValue(searchInput.trim());
  const [query, setQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(PER_PAGE_OPTIONS[0]);
  const [users, setUsers] = useState<Array<ReturnType<typeof mapUser>>>([]);
  const [pageInfo, setPageInfo] = useState<{
    nextPageToken?: string;
    previousPageToken?: string;
    totalCount?: number;
    hasNextPage?: boolean;
    hasPreviousPage?: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [verifiedFilter, setVerifiedFilter] = useState<VerifiedFilter>("all");
  const [bannedFilter, setBannedFilter] = useState<BannedFilter>("all");

  useEffect(() => {
    setQuery(debouncedQuery);
    setPageIndex(1);
  }, [debouncedQuery]);

  useEffect(() => {
    // Lazy-load: skip the fetch entirely while the Scholarships tab is
    // active, e.g. a deep link straight to /users?tab=scholarships never
    // needs the users list at all.
    if (activeTab !== "All") return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const isVerified =
      verifiedFilter === "verified" ? true : verifiedFilter === "not_verified" ? false : null;
    const bannedOnly = bannedFilter === "banned";

    listUsers({ query, pageSize, page: Math.max(pageIndex - 1, 0), isVerified, bannedOnly })
      .then((data) => {
        if (cancelled) return;
        setUsers((data.users ?? []).map(mapUser));
        setPageInfo(data.pageInfo ?? null);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message ?? "Failed to load users");
        setUsers([]);
        setPageInfo(null);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, query, pageIndex, pageSize, verifiedFilter, bannedFilter]);

  const totalLabel = useMemo(() => {
    if (pageInfo?.totalCount && pageInfo.totalCount > 0) {
      return `Total Users: ${pageInfo.totalCount}`;
    }
    return `Total Users: ${users.length}`;
  }, [pageInfo, users.length]);

  const totalPages = useMemo(() => {
    if (pageInfo?.totalCount && pageInfo.totalCount > 0) {
      return Math.max(1, Math.ceil(pageInfo.totalCount / pageSize));
    }
    return pageIndex;
  }, [pageInfo?.totalCount, pageIndex, pageSize]);

  const pages = useMemo(() => buildPageList(totalPages, pageIndex), [totalPages, pageIndex]);
  const activePage = Math.min(pageIndex, totalPages);
  const hasPrevious = pageIndex > 1;
  const hasNext = pageInfo?.hasNextPage ?? (users.length === pageSize && users.length > 0);

  return (
    <div className={styles.page}>
      <div>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Users</h1>
        </div>
        <div className={styles.toolbar}>
          <ProfileTabs
            role="tablist"
            aria-label="User list"
            tabs={TABS.map((tab) => ({ id: tab, label: tab }))}
            activeTab={activeTab}
            onKeyDown={handleTabArrowKeyDown}
            onTabChange={(tab) => handleTabChange(tab as Tab)}
          />
          {activeTab === "All" ? (
            <div className={styles.filters}>
              <SearchField
                className={styles.searchField}
                icon={<MagnifyingGlass size={14} />}
                label="Search users"
                placeholder="Search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.currentTarget.value)}
              />
              <div className={styles.filterGroup}>
                <FilterDropdown
                  label="Verified"
                  value={verifiedFilter}
                  options={VERIFIED_OPTIONS}
                  onSelect={(v) => {
                    setVerifiedFilter(v);
                    setPageIndex(1);
                  }}
                />
                <FilterDropdown
                  label="Banned"
                  value={bannedFilter}
                  options={BANNED_OPTIONS}
                  onSelect={(v) => {
                    setBannedFilter(v);
                    setPageIndex(1);
                  }}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {activeTab === "Scholarships" ? (
        <ScholarshipsManageView />
      ) : (
        <>
          <section className={styles.tableSection}>
            {loading ? <LoadingOverlay label="Loading users..." /> : null}
            <DataTable
              columns="minmax(180px, 1.2fr) minmax(220px, 1.5fr) minmax(120px, 0.9fr) 120px 120px minmax(180px, 1.1fr)"
              header={["Username", "E-mail", "Tier", "Status", "Joined date", "Actions"]}
              headerClassName={styles.tableHeader}
            >
              {users.map((user) => (
                <DataRow key={user.id} onActivate={() => navigate(`/users/${user.handle}`)}>
                  <div className={styles.userCell}>
                    <UserAvatar avatarUrl={user.avatarUrl} initials={user.initials} />
                    {user.handle && user.handle !== "unknown" ? (
                      <Link
                        to={`/users/${user.handle}`}
                        className={styles.userLink}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {user.username}
                      </Link>
                    ) : (
                      <span className={styles.meta}>{user.username}</span>
                    )}
                  </div>
                  <span className={styles.meta} data-label="E-mail">
                    {user.email}
                  </span>
                  <span className={styles.meta} data-label="Tier">
                    {user.tier}
                  </span>
                  <span className={styles.statusBadge}>{user.status}</span>
                  <span className={styles.meta} data-label="Joined date">
                    {user.joined}
                  </span>
                  <div
                    className={styles.actions}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <IconButton aria-label="Moderation actions" variant="outline" size="lg">
                      <Gavel size={14} />
                    </IconButton>
                    <IconButton aria-label="Verify" variant="outline" size="lg">
                      <SealCheck size={14} />
                    </IconButton>
                    <Button
                      variant="link"
                      size="sm"
                      className={styles.viewLink}
                      render={<Link to={`/users/${user.handle}`} />}
                    >
                      View
                      <ArrowSquareOut size={14} />
                    </Button>
                  </div>
                </DataRow>
              ))}
            </DataTable>
            {!loading && error ? <div className={styles.emptyState}>{error}</div> : null}
            {!loading && !error && users.length === 0 ? (
              <div className={styles.emptyState}>No users found.</div>
            ) : null}
          </section>

          <PaginationFooter
            totalLabel={totalLabel}
            pages={pages}
            activePage={activePage}
            hasPrevious={hasPrevious}
            hasNext={hasNext}
            onPrevious={() => setPageIndex((page) => Math.max(1, page - 1))}
            onNext={() => setPageIndex((page) => page + 1)}
            perPage={pageSize}
            perPageOptions={PER_PAGE_OPTIONS}
            onPerPageChange={(option) => {
              setPageSize(option);
              setPageIndex(1);
            }}
            onPageChange={(page) => {
              if (page === activePage) return;
              setPageIndex(page);
            }}
            className={styles.tableFooter}
          />
        </>
      )}
    </div>
  );
}

function mapUser(user: {
  id?: string;
  username?: string;
  displayName?: string;
  email?: string;
  avatarUrl?: string;
  tier?: string;
  bannedAt?: string | null;
  createdAt?: string;
}) {
  const handle = user.username || user.id || "unknown";
  return {
    id: user.id ?? handle,
    handle,
    username: user.username ? `@${user.username}` : "—",
    email: user.email ?? "—",
    avatarUrl: user.avatarUrl,
    tier: formatTier(user.tier),
    status: user.bannedAt ? "Banned" : "Active",
    joined: formatDate(user.createdAt),
    initials: user.displayName ?? user.username ?? "",
  };
}

function UserAvatar({ avatarUrl, initials }: { avatarUrl?: string; initials: string }) {
  // The design-system's Avatar falls back to the initials on its own when the
  // image is missing or fails to load, so the page no longer tracks that.
  return (
    <Avatar size="sm" className={`size-5 ${styles.avatar}`} aria-hidden="true">
      {avatarUrl ? <AvatarImage src={avatarUrl} alt="" loading="lazy" /> : null}
      <AvatarFallback className="text-[10px]">{initialsForMember(initials)}</AvatarFallback>
    </Avatar>
  );
}

function FilterDropdown<T extends string>({
  label,
  value,
  options,
  onSelect,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onSelect: (value: T) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", handleClick);
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("mousedown", handleClick);
      window.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const current = options.find((o) => o.value === value);
  const displayLabel = value === "all" ? label : `${label}: ${current?.label ?? value}`;

  return (
    <div className={styles.filterWrapper} ref={ref}>
      <button
        type="button"
        className={`${styles.filterButton} ${value !== "all" ? styles.filterActive : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{displayLabel}</span>
        <CaretDown size={10} />
      </button>
      {open ? (
        <div className={styles.filterMenu} role="listbox">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={`${styles.filterOption} ${option.value === value ? styles.filterOptionActive : ""}`}
              onClick={() => {
                onSelect(option.value);
                setOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
